import { Component, EventEmitter, Inject, Input, Output } from '@angular/core';

import { CurrencyItem, withModule } from '@skylab-shared/business';

import { FavouriteSwitchCommand, ProductQLCollectionItem } from '@skylab-angular-shared/business';
import { CommandBus } from '@skylab-angular-shared/core';
import { CurrencyPipe, TranslatePipe } from '@skylab-angular-shared/pipes';
import { StarComponent } from '@skylab-angular-shared/ui';
import { CalculationMethod, ExchangeRateModel } from '@skylab-shared/dataModel';
import { Calc, MidpointRounding } from '@skylab-shared/utils';

@Component({
    selector: 'skl-product-quick-list-item',
    template: `
        <div class="item-wrapper">
            <div class="item item--star">
                <skl-star class="custom-item__star" [value]="item.favourite" (click)="$event.stopPropagation(); switchFavourite()"></skl-star>
            </div>
            <div class="item item--name">
                <div class="text--ellipsis">{{item.name}}</div>
                @if (item.symbol) {
                    <div class="custom-item--sub text--ellipsis">{{'shared.scenes.product-quick-list-item.symbol.label' | translate}}: {{item.symbol}}</div>
                }
            </div>
            <div class="item item--price">
                <div>{{priceType === 'sale' ? salePrice : purchasePrice}}</div>
            </div>
            @if (isInventoryLevelVisible) {
                <div class="item item--level">
                    {{item.inventoryControl ? item.inventoryLevel + ' ' + item.unitSymbol : '-'}}
                </div>
            }
        </div>
    `,
    styleUrls: ['./product-quick-list-item.component.scss'],
    imports: [StarComponent, TranslatePipe]
})
export class ProductQuickListItemComponent {
    @Input() public item!: ProductQLCollectionItem;
    @Input() public calculationMethod!: CalculationMethod;
    @Input() public currency!: CurrencyItem;
    @Input() public exchangeRate!: ExchangeRateModel;
    @Input() public priceType: 'sale' | 'purchase' = 'sale';
    @Output() public onSwitch = new EventEmitter<void>();

    public constructor(
        private readonly currencyPipe: CurrencyPipe,
        @Inject(CommandBus) private readonly commandBus: CommandBus
    ) { }

    public async switchFavourite(): Promise<void> {
        const result = await this.commandBus.commandAsync(new FavouriteSwitchCommand({id: this.item.externalId, value: !this.item.favourite, type: 'product'}));
        if (result) {
            this.item.version = result.version;
            this.item.favourite = !this.item.favourite;
            this.onSwitch.emit();
        }
    }

    public get isInventoryLevelVisible(): boolean {
        return withModule('Inventory');
    }

    public get purchasePrice(): string {
        let price = [CalculationMethod.Net, CalculationMethod.Manual].includes(this.calculationMethod) ? this.item.purchaseNetPrice : this.item.purchaseGrossPrice;

        if (this.exchangeRate) {
            const rate = (this.exchangeRate.rate || 1) / (this.exchangeRate.units || 1);
            price = Calc.round(price / rate, this.currency.precision, MidpointRounding.AwayFromZero);
        }

        return this.currencyPipe.transform(price, { precision: this.currency.precision, symbol: this.currency.symbol });
    }

    public get salePrice(): string {
        const price = this.calculationMethod === CalculationMethod.Net ? this.item.netPrice : this.item.grossPrice;
        let currencyPrice = this.calculationMethod === CalculationMethod.Net ? this.item.netPriceCurrency : this.item.grossPriceCurrency;

        if (this.exchangeRate && currencyPrice == null) {
            const rate = (this.exchangeRate.rate || 1) / (this.exchangeRate.units || 1);
            currencyPrice = Calc.round(price / rate, this.currency.precision, MidpointRounding.AwayFromZero);
        }

        return this.currencyPipe.transform((this.exchangeRate && currencyPrice) ?? price, { precision: this.currency.precision, symbol: this.currency.symbol });
    }
}
